import React, {useEffect, useMemo, useState} from "react";

import {DishDetailsItem} from "@apps/dish/interfaces";

import {EventOrigin, recordEvent} from "@globals/config/eventUtils";
import getQVImageSizeGroup, {
  qvImageSizes,
} from "@globals/functions/getQVImageSizeGroup";
import {IMAGE_SIZES} from "@globals/functions/getResolutionImgUrl";
import positiveMod from "@globals/utils/positiveMod";

import DishDetails from "@components/DishDetails";
import NomiconIcon from "@components/NomiconIcon";
import TransparentButton from "@components/TransparentButton";

import styles from "./DishDetailsModal.scss";

export type DishDetailsModalProps = {
  dish: DishDetailsItem;
  openWithReviews?: boolean;
  menuItemList?: DishDetailsItem[];
  hidePurchaseBtnOverride?: boolean;
  onClose?: () => void;
};

const qvImageSizeGroup = getQVImageSizeGroup();

export default function DishDetailsModal({
  dish,
  openWithReviews,
  menuItemList,
  hidePurchaseBtnOverride,
  onClose,
}: DishDetailsModalProps): JSX.Element {
  useEffect(() => {
    recordEvent(EventOrigin.PAGE, "open QV", dish.id);
  }, []);

  const [indexOffset, setIndexOffset] = useState<number>(0);
  const dishIndex = menuItemList?.findIndex((item) => item.id === dish.id) ?? 0;

  const currentItem = useMemo(() => {
    if (!menuItemList) return dish;
    const index = positiveMod(dishIndex + indexOffset, menuItemList.length);
    return menuItemList[index];
  }, [menuItemList, dishIndex, indexOffset, dish]);

  const nextItem = () => setIndexOffset(indexOffset + 1);
  const prevItem = () => setIndexOffset(indexOffset - 1);
  const canNavigate = Boolean(menuItemList?.length);

  return (
    <>
      {canNavigate && (
        <TransparentButton
          onClick={nextItem}
          className={styles.nextItem}
          aria-label="Next dish item"
          data-testid="next-dish-item"
        >
          <NomiconIcon icon="nomicon-caret-right" />
        </TransparentButton>
      )}
      {canNavigate && (
        <TransparentButton
          onClick={prevItem}
          className={styles.prevItem}
          aria-label="Previous dish item"
          data-testid="prev-dish-item"
        >
          <NomiconIcon icon="nomicon-caret-left" />
        </TransparentButton>
      )}
      <div className={styles.overflowContainer}>
        <div className={styles.contentContainer}>
          <DishDetails
            onPurchaseButtonClick={onClose}
            dish={currentItem}
            imageSize={IMAGE_SIZES.QUICK_VIEW}
            imageSizeGroup={qvImageSizeGroup}
            sizesAttribute={qvImageSizes}
            mediaClassName={styles.dishDetailsMedia}
            contentClassName={styles.dishDetailsContent}
            titleClassName={styles.dishDetailsTitle}
            openReviews={openWithReviews}
            hidePurchaseBtnOverride={hidePurchaseBtnOverride}
          />
        </div>
      </div>
    </>
  );
}
